<template>
  <div class="modal-overlay" v-if="visible" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <span class="modal-icon">🔐</span>
        <h3>两步验证</h3>
        <button class="modal-close" @click="close">✕</button>
      </div>
      <div class="modal-body">
        <p class="modal-desc">{{ desc }}</p>
        <div class="modal-code-inputs">
          <input
            v-for="i in 6"
            :key="i"
            type="text"
            maxlength="1"
            class="code-input"
            v-model="twoFactorCode[i - 1]"
            @input="onCodeInput(i, $event)"
            @keydown="onCodeKeydown(i, $event)"
            ref="codeInputs"
          />
        </div>
        <div class="modal-error" v-if="twoFactorError">
          ⚠️ {{ twoFactorError }}
        </div>
        <div class="modal-actions">
          <button class="btn-modal-cancel" @click="close">取消</button>
          <button
            class="btn-modal-confirm"
            @click="handleConfirm"
            :disabled="!isComplete || isVerifying"
          >
            {{ isVerifying ? "⏳ 验证中..." : "✅ 确认" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TwoFactorModal",
  props: {
    desc: {
      type: String,
      default: "请输入您的两步验证码",
    },
  },
  data() {
    return {
      visible: false,
      twoFactorCode: ["", "", "", "", "", ""],
      twoFactorError: "",
      isVerifying: false,
    };
  },
  computed: {
    isComplete() {
      return this.twoFactorCode.every((c) => c !== "");
    },
  },
  methods: {
    // ===== 外部控制方法 =====
    open() {
      // 打开弹窗并初始化状态
      this.twoFactorCode = ["", "", "", "", "", ""];
      this.twoFactorError = "";
      this.isVerifying = false;
      this.visible = true;
      this.$nextTick(() => {
        if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
          this.$refs.codeInputs[0].focus();
        }
      });
    },
    close() {
      this.visible = false;
      this.isVerifying = false;
      this.twoFactorCode = ["", "", "", "", "", ""];
      this.twoFactorError = "";
    },
    setVerifying(val) {
      this.isVerifying = val;
    },
    setError(msg) {
      this.twoFactorError = msg;
      this.twoFactorCode = ["", "", "", "", "", ""];
      this.isVerifying = false;
      this.$nextTick(() => {
        if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
          this.$refs.codeInputs[0].focus();
        }
      });
    },

    // ===== 内部方法 =====
    handleConfirm() {
      if (!this.isComplete || this.isVerifying) return;
      const enteredCode = this.twoFactorCode.join("");
      // 通过回调通知外部，由外部决定验证逻辑
      this.$emit("confirm", enteredCode);
    },

    onCodeInput(index, event) {
      const value = event.target.value;
      if (value && !/^\d$/.test(value)) {
        this.twoFactorCode[index - 1] = "";
        return;
      }

      if (value && index < 6) {
        this.$refs.codeInputs[index].focus();
      }
    },

    onCodeKeydown(index, event) {
      if (event.key === "Backspace" && !event.target.value && index > 1) {
        this.$refs.codeInputs[index - 2].focus();
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: #1a1f35;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 28px 32px 32px;
  max-width: 440px;
  width: 100%;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.modal-icon {
  font-size: 28px;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  flex: 1;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  border: none;
  color: rgba(255, 255, 255, 0.3);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.modal-body {
  text-align: center;
}

.modal-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 20px 0;
}

/* ===== 6位验证码输入 ===== */
.modal-code-inputs {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 16px;
}

.code-input {
  width: 48px;
  height: 56px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 2px solid rgba(255, 255, 255, 0.06);
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  font-family: inherit;
  outline: none;
  transition: border-color 0.25s ease;
}

.code-input:focus {
  border-color: #00b4ff;
}

.modal-error {
  font-size: 13px;
  color: #ff6b6b;
  margin-bottom: 16px;
  min-height: 20px;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.btn-modal-cancel {
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
}

.btn-modal-cancel:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.btn-modal-confirm {
  flex: 2;
  padding: 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
}

.btn-modal-confirm:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(0, 100, 255, 0.25);
}

.btn-modal-confirm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .modal-content {
    padding: 24px 20px 28px;
    margin: 16px;
  }

  .code-input {
    width: 40px;
    height: 48px;
    font-size: 20px;
  }

  .modal-code-inputs {
    gap: 8px;
  }

  .modal-actions {
    flex-direction: column;
  }
}

@media (max-width: 400px) {
  .code-input {
    width: 36px;
    height: 42px;
    font-size: 18px;
  }

  .modal-code-inputs {
    gap: 6px;
  }
}
</style>
